import React from 'react';
import { I18n } from 'react-redux-i18n';
import { Button } from 'semantic-ui-react';
import { formatDateByCountry } from '../../../helpers/dateAndTimeHelper';
import { AdminContextTypeApi, AdminRoleLevel } from '../../../modules/session';

const i18nKeys = {
  CONTEXT_TYPE_SYSTEM: 'account.eyes-only.contextType.system',
  CONTEXT_TYPE_ORGANIZATION: 'account.eyes-only.contextType.organization',
  CONTEXT_TYPE_CAMPAIGN: 'account.eyes-only.contextType.campaign',
  CONTEXT_TYPE_EVENT: 'account.eyes-only.contextType.event',
  CONTEXT_TYPE_CAMPAIGN_ORGANIZER: 'account.eyes-only.contextType.campaignOrganizer',
  CONTEXT_TYPE_EVENT_ORGANIZER: 'account.eyes-only.contextType.eventOrganizer',
  ADMIN_ROLE_OWNER: 'account.eyes-only.role.owner',
  ADMIN_ROLE_ADMIN: 'account.eyes-only.role.admin',
  ADMIN_ROLE_VIEWER: 'account.eyes-only.role.viewer',
  PROMOTE_ROLE_LABEL: 'account.eyes-only.promoteAndDemote.promoteRole.label',
  CREATED_AT_LABEL: 'account.eyes-only.createdAt.label',
  DEMOTE_SINGLE_BUTTON: 'account.eyes-only.demote.single-button',
  REVOKED_AT_LABEL: 'account.eyes-only.revokedAt.label',
  RESTRICTED_YES: 'account.eyes-only.restricted.yes'
};

/**
 * Get translated context type label from a contextType string.
 */
export const getContextTypeLabel = (contextType) => {
  const keyMap = {
    [AdminContextTypeApi.System]: i18nKeys.CONTEXT_TYPE_SYSTEM,
    [AdminContextTypeApi.Organization]: i18nKeys.CONTEXT_TYPE_ORGANIZATION,
    [AdminContextTypeApi.Campaign]: i18nKeys.CONTEXT_TYPE_CAMPAIGN,
    [AdminContextTypeApi.Event]: i18nKeys.CONTEXT_TYPE_EVENT,
    [AdminContextTypeApi.CampaignOrganizer]: i18nKeys.CONTEXT_TYPE_CAMPAIGN_ORGANIZER,
    [AdminContextTypeApi.EventOrganizer]: i18nKeys.CONTEXT_TYPE_EVENT_ORGANIZER
  };
  const key = keyMap[contextType];
  return key ? I18n.t(key) : contextType;
};

/**
 * Get translated role label from a role string.
 */
export const getRoleLabel = (role) => {
  const keyMap = {
    [AdminRoleLevel.Owner]: i18nKeys.ADMIN_ROLE_OWNER,
    [AdminRoleLevel.Admin]: i18nKeys.ADMIN_ROLE_ADMIN,
    [AdminRoleLevel.Viewer]: i18nKeys.ADMIN_ROLE_VIEWER
  };
  const key = keyMap[role];
  return key ? I18n.t(key) : role;
};

/**
 * Shared component for rendering a single admin membership item.
 *
 * Props:
 * - membership: the membership object (id, contextType, assignedEntityName, role, createdAtLocal, revokedAtLocal, restrictExports)
 * - onDemote: optional callback (membershipId) => void — when provided, shows a demote button
 * - isDemoteLoading: optional boolean — loading state for the demote button
 * - isExpired: optional boolean — visual indicator for revoked/expired memberships
 * - isCurrentUserSystemOwner: optional boolean — when true, allows demoting system admin/viewer memberships (not system owners)
 */
const MembershipItem = ({
  membership,
  onDemote,
  isDemoteLoading = false,
  isExpired = false,
  isCurrentUserSystemOwner = false
}) => {
  const isSystemMembership =
    membership.contextType === AdminContextTypeApi.System;
  const isOwnerMembership =
    membership.role === AdminRoleLevel.Owner;

  // Demote is available for:
  // - Non-system memberships (always, if onDemote provided)
  // - System memberships only if current user is a system owner AND the membership is not owner-level
  const showDemote = !!onDemote && (
    !isSystemMembership ||
    (isCurrentUserSystemOwner && !isOwnerMembership)
  );

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: isExpired
          ? 'var(--background-disabled, #f5f5f5)'
          : 'var(--background-primary, #fff)',
        border: `1px solid ${isExpired ? 'var(--border-disabled, #d0d0d0)' : 'var(--border-primary, #e0e0e0)'}`,
        borderRadius: '8px',
        marginBottom: '8px',
        opacity: isExpired ? 0.7 : 1
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
          {getContextTypeLabel(membership.contextType)}
          {membership.assignedEntityName && (
            <span
              style={{
                fontWeight: 400,
                marginLeft: '8px',
                color: 'var(--text-secondary, #666)'
              }}
            >
              - {membership.assignedEntityName}
            </span>
          )}
        </div>
        <div
          style={{ fontSize: '14px', color: 'var(--text-secondary, #666)' }}
        >
          <span>
            {I18n.t(i18nKeys.PROMOTE_ROLE_LABEL)}: {getRoleLabel(membership.role)}
          </span>
          <span style={{ marginLeft: '16px' }}>
            {I18n.t(i18nKeys.CREATED_AT_LABEL)}:{' '}
            {formatDateByCountry(membership.createdAtLocal)}
          </span>
          {membership.restrictExports && (
            <span style={{ marginLeft: '16px', color: 'var(--text-warning, #b45309)', fontWeight: 600 }}>
              {I18n.t(i18nKeys.RESTRICTED_YES)}
            </span>
          )}
          {isExpired && membership.revokedAtLocal && (
            <span style={{ marginLeft: '16px' }}>
              {I18n.t(i18nKeys.REVOKED_AT_LABEL)}:{' '}
              {formatDateByCountry(membership.revokedAtLocal)}
            </span>
          )}
        </div>
      </div>
      {showDemote && (
        <Button
          size="small"
          loading={isDemoteLoading}
          onClick={() => onDemote(membership.id)}
        >
          {I18n.t(i18nKeys.DEMOTE_SINGLE_BUTTON)}
        </Button>
      )}
    </div>
  );
};

export default MembershipItem;
